'use client';

import { Fragment, type ReactElement } from 'react';
import { formatParts, type Message } from '@/i18n';
import { useLocale } from '@/lib/locale-context';
import styles from './values.module.css';

/**
 * A value's words, said once (docs/DESIGN_SYSTEM.md "Values and entries": the
 * accessible label is a sentence). The value's `Message` formats with the
 * visible parts in its slots (`shown`): what the eye reads stays where it is
 * and is read as it is, and the words around it, which the signs say to the
 * eye, are said to a screen reader only. So a warning item reads "Fix the
 * login, warning" while the eye sees the amber glyph and the title, and the
 * title is in the page once.
 *
 * A slot `shown` does not name keeps the message's own argument and is said
 * only; a message with no `shown` at all is said only (an avatar alone).
 *
 * `words="shown"` shows the words too, each trimmed in its own span: a
 * sentence whose words stand between its values (an entry's slotted
 * sentence, src/components/entry.tsx), set in a container whose gap spaces
 * the parts.
 * The one client leaf of the kit's words, the way `Msg` is for a plain
 * descriptor: the value components around it stay server-safe.
 */
export function Phrase({
  message,
  shown = {},
  words = 'said',
  wordsClassName,
}: {
  message: Message;
  /** The visible parts, by slot name. Elements, never strings: a string is an
   *  argument, and would be said only. */
  shown?: Readonly<Record<string, ReactElement>>;
  /** Whether the words around the parts are said only (the default) or shown. */
  words?: 'said' | 'shown';
  /** The shown words' class. */
  wordsClassName?: string;
}) {
  const locale = useLocale();
  const parts = formatParts<ReactElement>(locale, message.key, {
    ...message.args,
    ...shown,
  });
  return (
    <>
      {parts.map((part, i) =>
        typeof part === 'string' && words === 'shown' ? (
          part.trim() === '' ? null : (
            <span key={i} className={wordsClassName}>
              {part.trim()}
            </span>
          )
        ) : typeof part === 'string' ? (
          <span key={i} className={styles.srOnly}>
            {part}
          </span>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        ),
      )}
    </>
  );
}
